import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { lcApiBase, lcHttpError, lcStationId } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';
import { ProduitItem, ProduitsPageResult } from './logiciel-caisse-produit.service';

interface ReasortProductsResponse {
  success: boolean;
  produits?: ProduitItem[];
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  meta?: { total?: number; page?: number; limit?: number; pages?: number };
  error?: string;
}

interface ReasortBulkResponse {
  success: boolean;
  updated?: number;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseReasortService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  getProduitsForEntrepot(
    entrepotId: number,
    options: {
      actif: boolean;
      page?: number;
      limit?: number;
      byGroup?: boolean;
      q?: string;
      taille?: string;
      couleur?: string;
      prix?: number;
      prixOp?: string;
      stockState?: 'in_stock' | 'low_stock' | 'out_of_stock';
      entrepotIds?: number[];
      stockPhysMin?: number;
      stockPhysMax?: number;
    }
  ): Observable<ProduitsPageResult> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }

    let params = new HttpParams();
    const page = options.page ?? 1;
    const limit = options.limit ?? 10;
    params = params.set('actif', options.actif ? 'true' : 'false');
    params = params.set('page', String(Math.max(1, page)));
    params = params.set('limit', String(Math.min(10000, Math.max(1, limit))));
    if (options.byGroup) {
      params = params.set('by_group', 'true');
    }
    const kw = String(options.q ?? '').trim();
    if (kw) {
      params = params.set('q', kw.slice(0, 120));
    }
    const taille = String(options.taille ?? '').trim();
    if (taille) {
      params = params.set('taille', taille);
    }
    const couleur = String(options.couleur ?? '').trim();
    if (couleur) {
      params = params.set('couleur', couleur);
    }
    if (options.prix != null && Number.isFinite(options.prix)) {
      params = params.set('prix', String(options.prix));
      params = params.set('prix_op', options.prixOp ?? '=');
    }
    if (options.stockState) {
      params = params.set('stock_state', options.stockState);
    }
    if (options.entrepotIds?.length) {
      params = params.set('entrepot_ids', options.entrepotIds.join(','));
    }
    if (options.stockPhysMin != null) {
      params = params.set('stock_phys_min', String(options.stockPhysMin));
    }
    if (options.stockPhysMax != null) {
      params = params.set('stock_phys_max', String(options.stockPhysMax));
    }

    const url = `${lcApiBase(this.authService)}/api/reasort-config/station/${stationId}/entrepot/${entrepotId}/products`;
    return this.http.get<ReasortProductsResponse>(url, { params }).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.produits)) {
          throw new Error(res?.error || 'Réponse réassort invalide');
        }
        const total = res.total ?? res.meta?.total ?? res.produits.length;
        const p = res.page ?? res.meta?.page ?? page;
        const l = res.limit ?? res.meta?.limit ?? limit;
        const pages = res.totalPages ?? res.meta?.pages ?? Math.max(1, Math.ceil(total / l));
        return {
          items: res.produits,
          pagination: { page: p, limit: l, total, totalPages: pages },
        };
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur chargement produits (réassort).'))
      )
    );
  }

  bulkUpsert(
    entrepotId: number,
    body: {
      min_stock: number;
      max_stock: number;
      product_ids?: number[];
      apply_all?: boolean;
      actif_only?: boolean;
    }
  ): Observable<number> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/reasort-config/station/${stationId}/entrepot/${entrepotId}/bulk`;
    return this.http.post<ReasortBulkResponse>(url, body).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Mise à jour refusée');
        }
        return res.updated ?? 0;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur enregistrement réassort.'))
      )
    );
  }
}
